import { useEffect, useRef, useState } from "react";
import { DateDisplay } from "../../../../../../commonComponents/inline/dateDisplay/dateDisplay";
import { ArticleExtraData } from "../../../../../../../articlePage/stateInterfaces";
import { SiteItemData } from "../../../../../../interfaces";
import { disablePageRefresh, enablePageRefresh } from "../../../../../../siteWidgets/siteWidgetUtils";
import { FlashesMediaItem } from "../../../../../verticalVideoOverlayPlayer/designs/flashesMediaItem/flashesMediaItem";
import { VerticalVideoOverlayPlayer } from "../../../../../verticalVideoOverlayPlayer/verticalVideoOverlayPlayer";
import { getTranslation } from "../../../../autoTickerRowWidget/autoTickerRowTranslations";
import { categoryObj } from "../../../../newYnetTvVerticalDynamic/components/NewYnetTvVerticalDynamicMutamComponenta";
import { getImageUrl } from "config/apiUrls";
import { isWcmEditor } from "../../../../../../../config/serverConfig";
import { minutesDiff } from "../../newsFlashAndTwentyFourSevenMobileComponenta";
import { mapArticleExtraDataToOverlayItems } from "../utils/newsFlashUtils";
require("./m_ynet_ongoingRoofEventPopup.less");

interface OngoingRoofEventPopupProps {
    isOpen: boolean
    roof: string
    articles: ArticleExtraData[]
    isLTR: boolean
    domain: string
    languageCode: string
    category: categoryObj
    highlightingDuration: number
    verticalBackgroundImage?: string
    onClose: () => void
}

const LIVE_UPDATES_LABEL = "לעדכונים שוטפים";
const CLOSE_ANIMATION_MS = 280;
const OPEN_ANIMATION_MS = 320;
const SHEET_TRANSFORM_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";
const BOTTOM_AD_ID = "ads.banner";
const PIKUD_HAOREF_SELECTOR = ".pikudHaorefAlert";
const mediaAspectRatios = {
    "6:7": { w: 215, h: 251 },
    "16:9": { w: 448, h: 251 },
    "9:16": { w: 207, h: 370 },
};

/** How much viewport space a fixed bottom element occupies (from its top to the viewport bottom). */
function getElementBottomClearance(el: Element | null): number {
    if (!el || !(el instanceof HTMLElement)) {
        return 0;
    }
    const rect = el.getBoundingClientRect();
    if (rect.height <= 0) {
        return 0;
    }
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    // Ignore elements that are not intersecting the bottom of the viewport
    if (rect.bottom < viewportHeight - 1 || rect.top >= viewportHeight) {
        return 0;
    }
    return Math.max(0, Math.ceil(viewportHeight - rect.top));
}

function measureBottomObstructionHeight(): number {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return 0;
    }
    const adClearance = getElementBottomClearance(document.getElementById(BOTTOM_AD_ID));
    const pikudClearance = getElementBottomClearance(document.querySelector(PIKUD_HAOREF_SELECTOR));
    // Take the max: when pikud sits above the ad, its clearance already includes the ad height
    return Math.max(adClearance, pikudClearance);
}

function getPosterUrl(article: ArticleExtraData) {
    const { specialData, promotionImageDetails, video } = article;
    if (!specialData) {
        return undefined;
    }
    const { mediaLinkType, mediaProportion } = specialData;
    const mediaPosterSize = mediaLinkType !== "none" && mediaLinkType !== undefined && mediaProportion
        ? mediaAspectRatios[mediaProportion]
        : { w: 448, h: 251 };

    if (mediaLinkType === "image" && promotionImageDetails && (promotionImageDetails.url || promotionImageDetails.publishedLink)) {
        return getImageUrl(promotionImageDetails, mediaPosterSize);
    }
    if (mediaLinkType === "video" && video && video[0] && video[0].posterSrc) {
        return video[0].posterSrc;
    }
    return undefined;
}

export const OngoingRoofEventPopup = (props: OngoingRoofEventPopupProps) => {
    const { isOpen, roof, articles, isLTR, domain, languageCode, category, highlightingDuration, verticalBackgroundImage, onClose } = props;

    const [expandedItemIds, setExpandedItemIds] = useState<string[]>([]);
    const [selectedVideo, setSelectedVideo] = useState(-1);
    const [isMediaOverlayOpen, setIsMediaOverlayOpen] = useState(false);
    const [dragStartY, setDragStartY] = useState<number | null>(null);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const [isClosing, setIsClosing] = useState(false);
    const [isEntered, setIsEntered] = useState(false);
    const [bottomOffset, setBottomOffset] = useState<number>(measureBottomObstructionHeight());
    const closeTimerRef: any = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isOpen) {
            setExpandedItemIds([]);
            setDragOffsetY(0);
            setIsClosing(false);
            setIsEntered(false);
            disablePageRefresh();
        } else if (!isMediaOverlayOpen) {
            enablePageRefresh();
        }
    }, [isOpen]);

    // Slide the sheet in via transform transition (same mechanism as close/drag)
    useEffect(() => {
        if (!isOpen || isClosing) {
            return;
        }
        const rafId = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setIsEntered(true);
            });
        });
        return () => cancelAnimationFrame(rafId);
    }, [isOpen, isClosing]);

    useEffect(() => {
        if (!isOpen) {
            setBottomOffset(0);
            return;
        }

        let rafId: number | null = null;
        const observedElements: Element[] = [];
        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(() => scheduleUpdate())
            : null;

        const observeElement = (el: Element | null) => {
            if (!el || !resizeObserver || observedElements.indexOf(el) !== -1) {
                return;
            }
            resizeObserver.observe(el);
            observedElements.push(el);
        };

        const updateBottomOffset = () => {
            setBottomOffset(measureBottomObstructionHeight());
        };

        const scheduleUpdate = () => {
            if (rafId != null) {
                return;
            }
            rafId = requestAnimationFrame(() => {
                rafId = null;
                observeElement(document.getElementById(BOTTOM_AD_ID));
                observeElement(document.querySelector(PIKUD_HAOREF_SELECTOR));
                updateBottomOffset();
            });
        };

        observeElement(document.getElementById(BOTTOM_AD_ID));
        observeElement(document.querySelector(PIKUD_HAOREF_SELECTOR));
        updateBottomOffset();

        // Pikud HaOref / ads mount dynamically; keep clearance in sync while popup is open
        const mutationObserver = typeof MutationObserver !== "undefined"
            ? new MutationObserver(scheduleUpdate)
            : null;

        if (mutationObserver) {
            mutationObserver.observe(document.body, { childList: true, subtree: true });
        }

        window.addEventListener("resize", scheduleUpdate);

        return () => {
            window.removeEventListener("resize", scheduleUpdate);
            if (rafId != null) {
                cancelAnimationFrame(rafId);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (mutationObserver) {
                mutationObserver.disconnect();
            }
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isClosing) {
            return;
        }
        const offScreenY = typeof window !== "undefined" ? window.innerHeight : 1000;
        // After paint at the held drag Y (with transition enabled), animate off-screen
        const rafId = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setDragOffsetY(offScreenY);
            });
        });
        return () => cancelAnimationFrame(rafId);
    }, [isClosing]);

    useEffect(() => {
        return () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
            }
            if (isOpen && !isMediaOverlayOpen) {
                enablePageRefresh();
            }
        };
    }, [isOpen, isMediaOverlayOpen]);

    const requestClose = () => {
        if (isClosing) {
            return;
        }
        const fromY = Math.max(dragOffsetY, 0);
        // Hold at the current drag Y, then animate the rest of the way off-screen
        setIsClosing(true);
        setDragStartY(null);
        setDragOffsetY(fromY);
        closeTimerRef.current = setTimeout(() => {
            onClose();
            setIsClosing(false);
            setDragOffsetY(0);
            closeTimerRef.current = null;
        }, CLOSE_ANIMATION_MS);
    };

    const toggleItem = (articleId: string, title: string) => {
        const currentlyOpen = expandedItemIds.indexOf(articleId) !== -1;
        setExpandedItemIds(currentlyOpen
            ? expandedItemIds.filter(id => id !== articleId)
            : expandedItemIds.concat(articleId));
        document.dispatchEvent(
            new CustomEvent("newsFlashesTwentyFourSevenReadMoreChange", {
                detail: {
                    action: currentlyOpen ? "Collapse" : "Expand",
                    label: title,
                },
            }),
        );
    };

    const openMedia = (index: number) => {
        setIsMediaOverlayOpen(true);
        setSelectedVideo(index);
        disablePageRefresh();
    };

    const closeMedia = () => {
        setIsMediaOverlayOpen(false);
        setSelectedVideo(-1);
        if (isOpen) {
            disablePageRefresh();
        } else {
            enablePageRefresh();
        }
    };

    const onHandleTouchStart = (e: React.TouchEvent) => {
        if (isClosing) {
            return;
        }
        setDragStartY(e.touches[0].clientY);
    };

    const onHandleTouchMove = (e: React.TouchEvent) => {
        if (dragStartY == null || isClosing) {
            return;
        }
        const delta = e.touches[0].clientY - dragStartY;
        if (delta > 0) {
            setDragOffsetY(delta);
        }
    };

    const onHandleTouchEnd = () => {
        if (dragOffsetY > 80) {
            requestClose();
        } else {
            setDragOffsetY(0);
        }
        setDragStartY(null);
    };

    const onBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            requestClose();
        }
    };

    const renderDate = (article: ArticleExtraData) => {
        const lang = languageCode ? languageCode.slice(0, 2) : "he";
        const isRecentItem = highlightingDuration != null && minutesDiff(article) < (highlightingDuration || 1);
        return isRecentItem
            ? getTranslation(lang, "Now")
            : <DateDisplay date={article.dateUpdatedOnSite} isMinutesAgo={true} isArticle={true} />;
    };

    const renderItem = (article: ArticleExtraData, index: number, isFirst: boolean) => {
        const { specialData, video, promotionImageDetails, title, plainText, author, articleId } = article;
        const mediaLinkType = specialData && specialData.mediaLinkType;
        const mediaProportion = specialData && specialData.mediaProportion;
        const posterUrl = getPosterUrl(article);
        const hasImage = !!posterUrl;
        const itemIsOpen = expandedItemIds.indexOf(articleId) !== -1;
        const isPortrait = mediaProportion === "9:16";
        const mediaId = !isWcmEditor() && mediaLinkType === "video" && video && video[0] && video[0].mediaVideoId
            ? video[0].mediaVideoId
            : "";
        const mediaCredit = mediaLinkType === "video"
            ? ((specialData && specialData.videoCredit) || (video && video[0] && video[0].credit ? video[0].credit : undefined))
            : promotionImageDetails && promotionImageDetails.credit
                ? promotionImageDetails.credit
                : undefined;
        const isAuthor = author && author.length > 0;

        return (
            <div
                key={articleId}
                className={`ongoing-popup-item ${itemIsOpen ? "open" : ""} ${isFirst ? "is-first" : ""} ${hasImage ? "hasImage" : ""}`}
            >
                <div className="ongoing-popup-item-timeline" aria-hidden="true">
                    <div className={`ongoing-popup-dot ${isFirst ? "filled" : "white"}`} />
                </div>

                <div className="ongoing-popup-item-body">
                    <div className="ongoing-popup-item-header">
                        {hasImage &&
                            <div className={`ongoing-popup-image ${isPortrait ? "portrait" : ""}`} onClick={() => openMedia(index)} data_mediaid_analitycs={mediaId}>
                                <img className="posterImage" src={posterUrl} alt={title} />
                                {mediaLinkType === "video" && <div className="video-icon" />}
                                {mediaLinkType === "image" && <div className="expand-image-icon" />}
                            </div>
                        }

                        <div className="ongoing-popup-item-text" onClick={() => toggleItem(articleId, title)}>
                            <div className="ongoing-popup-item-title">{title}</div>
                            {!itemIsOpen && <div className="ongoing-popup-item-date">{renderDate(article)}</div>}
                        </div>
                    </div>

                    {itemIsOpen &&
                        <div className="ongoing-popup-item-content" onClick={() => toggleItem(articleId, title)}>
                            <div className="ongoing-popup-item-plain">{plainText}</div>

                            {isAuthor &&
                                <div className="ongoing-popup-item-author">
                                    {`(${author}${mediaCredit ? `, ${mediaCredit}` : ""})`}
                                </div>
                            }

                            <div className="ongoing-popup-item-date">{renderDate(article)}</div>
                        </div>
                    }
                </div>
            </div>
        );
    };

    if (!isOpen) {
        return null;
    }

    const lang = languageCode ? languageCode.slice(0, 2) : "he";
    const overlayTitle = getTranslation(lang, "News Flash");
    const overlayItems: SiteItemData[] = mapArticleExtraDataToOverlayItems(articles, verticalBackgroundImage).map(item => {
        const minutesSincePublished = Math.floor((new Date().getTime() - new Date(item.dateUpdatedOnSite).getTime()) / (1000 * 60));
        const isRecentItem = minutesSincePublished < (highlightingDuration || 1);
        const date = isRecentItem ? getTranslation(lang, "Now") : <DateDisplay date={item.dateUpdatedOnSite} isMinutesAgo={true} isArticle={true} />;
        return { ...item, date };
    });

    const isDragging = dragStartY != null;
    // Hold off-screen with no transition on first paint, then enable transition to slide in
    const shouldAnimateTransform = !isDragging && (isClosing || isEntered);
    const sheetTransition = shouldAnimateTransform
        ? `transform ${(isClosing ? CLOSE_ANIMATION_MS : OPEN_ANIMATION_MS)}ms ${SHEET_TRANSFORM_EASING}`
        : "none";

    let sheetTransform: string;
    if (isClosing || dragOffsetY > 0) {
        sheetTransform = `translateY(${dragOffsetY}px)`;
    } else if (!isEntered) {
        sheetTransform = "translateY(100%)";
    } else {
        sheetTransform = "translateY(0)";
    }

    const sheetStyle = {
        transform: sheetTransform,
        transition: sheetTransition,
    } as React.CSSProperties;

    const overlayStyle = bottomOffset > 0
        ? { bottom: bottomOffset } as React.CSSProperties
        : undefined;

    return (
        <div
            className={`ongoing-roof-event-popup-overlay ${isClosing ? "is-closing" : ""}`}
            onClick={onBackdropClick}
            style={overlayStyle}
        >
            <div className="ongoing-roof-event-popup-sheet" onClick={e => e.stopPropagation()} style={sheetStyle}>
                <div className="ongoing-popup-handle-area" onTouchStart={onHandleTouchStart} onTouchMove={onHandleTouchMove} onTouchEnd={onHandleTouchEnd}>
                    <div className="ongoing-popup-handle" />
                </div>

                <div className="ongoing-popup-roof-title">{roof}</div>

                <div className="ongoing-popup-scroll">
                    <div className="ongoing-popup-list">
                        <div className="ongoing-popup-list-connector" aria-hidden="true" />

                        {articles.map((article, index) => renderItem(article, index, index === 0))}
                    </div>
                </div>
            </div>

            <VerticalVideoOverlayPlayer
                isLTR={isLTR}
                domain={domain}
                isMobile={true}
                category={category}
                index={selectedVideo}
                isTitleVisible={true}
                showPrerollAds={false}
                isSubTitleVisible={true}
                videoItems={overlayItems}
                overlayTitle={overlayTitle}
                languageCode={languageCode}
                onVideoOverlayClose={closeMedia}
                isOverlayOpen={isMediaOverlayOpen}
                selectedVideoIndex={selectedVideo}
                ItemComponent={(playerProps) => <FlashesMediaItem {...playerProps} />}
            />
        </div>
    );
}
